import {AppDispatch} from 'redux/config'
import {IDToken, OktaAuth} from '@okta/okta-auth-js'

import {authAC} from 'redux/auth/auth.duck'
import defaultAuthState, {AuthUserStateType} from 'redux/auth/state'

import {getNameInitials} from 'helpers/stringUtility'

export type AuthClientType = typeof OktaAuth

export const isAuthenticationEnabled = () => {
  const enabled =  __WEBPACK_ISPROD__ ? globalThis.import_meta_env.OKTA_ENABLED : OKTA_ENABLED
  return enabled === 'true'
}

export const getToken = () => {
  let token
  if (Object.prototype.hasOwnProperty.call(localStorage, 'okta-token-storage')) {
    token = JSON.parse(localStorage['okta-token-storage'])?.idToken ?? undefined
  }

  return token
}

export const isAuthenticated = () => {
  const idToken = getToken()
  const tokenValue = idToken?.idToken ?? undefined

  return !!tokenValue
}

export const isTokenExpired = () => {
  const token = getToken()
  if (!token) return true

  const {expiresAt} = token
  if (!expiresAt) return true

  const now = Date.now()
  return now - expiresAt*1000 > 0
}

export const renewToken = async (authClient: AuthClientType, tokenName: string) => {
  await authClient.tokenManager.renew(tokenName)
}

export const getUser = (idToken: typeof IDToken): AuthUserStateType => {
  const name = idToken?.claims?.name ?? ''
  const email = idToken?.claims?.email ?? ''
  const initials = getNameInitials(idToken?.claims?.name ?? '')

  const groups = idToken?.claims?.groups ?? ['']

  const adminGroupName = __WEBPACK_ISPROD__ ? globalThis.import_meta_env.OKTA_ADMIN_GROUP_NAME : OKTA_ADMIN_GROUP_NAME
  const powerUserGroupName = __WEBPACK_ISPROD__ ? globalThis.import_meta_env.OKTA_POWER_USER_GROUP_NAME : OKTA_POWER_USER_GROUP_NAME

  const isAdmin = adminGroupName ? groups.includes(adminGroupName) : true
  const isPowerUser = powerUserGroupName ? groups.includes(powerUserGroupName) : true

  return {
    isAdmin,
    name,
    email,
    initials,
    isPowerUser,
  }
}

export const registerTokenEvents = (authClient: AuthClientType) => {
  authClient.tokenManager.on('expired', async function (key) {
    if (key === 'idToken') {
      await renewToken(authClient, 'idToken')
    }
  })

  authClient.tokenManager.on('error', async function (err) {
    if (err.errorCode === 'login_required') {
      await login(authClient)
    }
  })
}

export const authChangesSubscriber = (authState, dispatch) => {
  if (!authState.isAuthenticated) {
    // client is NOT authenticated
    dispatch(authAC.clientCleared())
    return
  }

  // client is authenticated
  let user: AuthUserStateType = defaultAuthState.user
  const idToken = authState?.idToken
  if (idToken) {
    user = getUser(idToken)
  }

  dispatch(authAC.clientAuthenticated(user))
}

export const subscribeToAuthChanges = (authClient: AuthClientType, dispatch: AppDispatch) => {
  authClient.authStateManager.subscribe(authClient => authChangesSubscriber(authClient, dispatch))
}

export const handleAuth = async (authClient: AuthClientType, dispatch: AppDispatch) => {
  const idToken: typeof IDToken = await authClient.tokenManager.get('idToken')
  if (idToken) {
    const user = getUser(idToken)
    dispatch(authAC.clientAuthenticated(user))

    await authClient.start()
  }
}

export const login = async (authClient: AuthClientType) => {
  if (!authClient.token.isLoginRedirect()) {
    await authClient.token.getWithRedirect({
      responseType: ['id_token'],
    })
  }
}

export const logout = async (authClient: AuthClientType) => {
  await authClient.stop()
  await authClient.signOut({
    clearTokensBeforeRedirect: true,
    postLogoutRedirectUri: window.location.origin,
  })
}

export const loginCallback = async (authClient: AuthClientType) => {
  if (authClient.token.isLoginRedirect()) {
    const {tokens} = await authClient.token.parseFromUrl()
    authClient.tokenManager.setTokens(tokens)
  }
}
